import { GameSurfaceIntegrated } from "../../state/GameSurfaceVisibility";
import { useContext, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Check, Lightbulb, Sparkles, Target, X } from "lucide-react";
import { COGNITIVE_LABEL, TYPE_LABEL } from "../../engine";
import type { Cognitive, QuestionType } from "../../engine";
import { CONCEPT_MAP, type ConceptId } from "../../data/concepts";
import {
  learningUnitCompletionForAnswer,
} from "../../lib/learningEvidence";
import { useGame } from "../../state/GameProvider";

import { useDialog } from "../../lib/useDialog";
import { AutoAdvance } from "./AutoAdvance";

import { buildAnswerCapture, saveAnswerCapture, captureService, type AnswerCapture } from "../../lib/learningRecords";

const INDEX = ["一", "二", "三", "四", "五", "六"];

export function ChallengeOverlay() {
  const integrated = useContext(GameSurfaceIntegrated);
  const { state, actions, submitCompletion } = useGame();
  const { duel } = state;
  const challenge = duel.challenge;

  const dialogRef = useRef<HTMLElement>(null);
  const answeredRef = useRef<typeof challenge>(null);
  const currentChallenge = useRef(challenge);
  currentChallenge.current = challenge;
  const pendingCapture = useRef<{ challenge: NonNullable<typeof challenge>; capture: AnswerCapture; completion: NonNullable<ReturnType<typeof learningUnitCompletionForAnswer>> } | null>(null);
  const [recordError, setRecordError] = useState<"storage" | "account" | null>(null);
  useDialog(dialogRef, Boolean(challenge), challenge, actions.close, index => {
    const option = challenge?.options[index];
    if (option && !duel.selected && option.id !== duel.hintedOut) answer(option.id);
  });

  const decided = Boolean(duel.selected);
  const correct = duel.result === "correct";
  const won = duel.status === "won";
  const primaryLabel = !correct ? "回到牌桌" : won ? "查看戰果" : "繼續攻勢";

  async function answer(optionId: string) {
    if (!challenge || answeredRef.current === challenge) return;
    const existing = pendingCapture.current?.challenge === challenge ? pendingCapture.current : null;
    const completion = existing?.completion || learningUnitCompletionForAnswer(state, optionId);
    if (!completion) return;
    answeredRef.current = challenge;
    setRecordError(null);
    try {
      const capture = existing?.capture || buildAnswerCapture(challenge, optionId, completion);
      pendingCapture.current = { challenge, capture, completion };
      await saveAnswerCapture(capture);
      if (currentChallenge.current !== challenge) return;
      if (captureService().scope !== capture.scope) {
        pendingCapture.current = null;
        answeredRef.current = null;
        setRecordError("account");
        return;
      }
      // Retry the original saved choice, never change its identity or payload.
      const selected = String((capture.operations[0].content as { selectedOptionId: string }).selectedOptionId);
      actions.answer(selected);
      submitCompletion(completion);
      pendingCapture.current = null;
    } catch {
      if (currentChallenge.current === challenge) { answeredRef.current = null; setRecordError("storage"); }
    }
  }

  useEffect(() => {
    if (duel.selected) {
      const feedback = dialogRef.current?.querySelector<HTMLElement>(".ch-feedback");
      feedback?.focus({ preventScroll: true });
      feedback?.scrollIntoView({ block: "nearest" });
    }
  }, [duel.selected]);

  return (
    <AnimatePresence>
      {challenge && (
        <motion.div
          className="overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={actions.close}
        >
          <motion.section
            ref={dialogRef}
            tabIndex={-1}
            className={`challenge tone-${challenge.gate}${duel.result === "correct" ? " celebrate" : ""}${duel.result === "wrong" ? " stumble" : ""}`}
            initial={{ opacity: 0, y: 18, rotateY: -76, scale: 0.92 }}
            animate={{ opacity: 1, y: 0, rotateY: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, rotateY: 18, scale: 0.96 }}
            transition={{ type: "spring", stiffness: 220, damping: 22 }}
            style={{ transformPerspective: 900, transformStyle: "preserve-3d" }}
            onClick={(event) => event.stopPropagation()}
            role="dialog"
            aria-modal={!integrated}
            aria-label={challenge.prompt}
          >
            <div className="ch-head">
              <span className="ch-prompt">
                <Target size={14} />
                <span>{challenge.prompt}</span>
              </span>
              {!decided && (
                <button
                  className="ch-hint"
                  onClick={actions.hint}
                  disabled={Boolean(duel.hintedOut)}
                  title="花 1 明點，排除一個錯項"
                >
                  <Lightbulb size={13} />
                  <span>{duel.hintedOut ? "已燭照" : "燭照"}</span>
                </button>
              )}
              <button className="ch-close" onClick={actions.close} aria-label="關閉">
                <X size={17} />
              </button>
            </div>

            {(challenge.itemType || challenge.route === "concept") && (
              <div className="ch-tags">
                {challenge.itemType && <span className="tag type">{TYPE_LABEL[challenge.itemType as QuestionType]}</span>}
                {challenge.cognitive && (
                  <span className="tag cog">{COGNITIVE_LABEL[challenge.cognitive as Cognitive]}</span>
                )}
                {challenge.concepts?.slice(0, 2).map((id) => (
                  <span key={id} className="tag concept">
                    {CONCEPT_MAP.get(id as ConceptId)?.label ?? id}
                  </span>
                ))}
                {challenge.route === "concept" && <span className="tag route">概念題 · 不限本章</span>}
              </div>
            )}

            {recordError && <p role="alert">{recordError === "account" ? "帳號狀態已變更，剛才的記錄保留原歸屬；請確認帳號後再作答。" : "作答尚未完整保存，請保留此頁並點選原答案重試。"}</p>}
            {integrated && challenge.source !== "bank" && <p className="reading-note">基礎補充練習 · 本題練習字義或句意，不能單獨證明已具備情境運用、跨章理解能力。</p>}
            <p className="keyboard-hint">可按 1–4 選答 · Esc 返回牌桌</p>
            <h2 className="ch-stem">{challenge.stem}</h2>
            {challenge.context && <blockquote className="ch-context">{challenge.context}</blockquote>}

            <div className="ch-options">
              {challenge.options.map((option, index) => {
                const isAnswer = option.id === challenge.answerId;
                const isSelected = option.id === duel.selected;
                const culled = option.id === duel.hintedOut;
                const showAnswer = decided && isAnswer;
                const showWrong = decided && isSelected && !isAnswer;
                const dimmed = (decided && !isAnswer && !isSelected) || (!decided && culled);
                // Every wrong option carries its own diagnosis. Revealing it
                // for the one they picked *and* the rest turns a single
                // question into four small lessons.
                const why = decided && !isAnswer ? challenge.optionWhy?.[option.id] : undefined;
                return (
                  <button
                    key={option.id}
                    className={`opt${showAnswer ? " is-answer" : ""}${showWrong ? " is-wrong" : ""}${
                      dimmed ? " dimmed" : ""
                    }${culled ? " is-culled" : ""}`}
                    disabled={decided || culled || (recordError === "storage" && (pendingCapture.current?.capture.operations[0].content as { selectedOptionId?: string })?.selectedOptionId !== option.id)}
                    onClick={() => answer(option.id)}
                  >
                    <span className="opt-mark">
                      {showAnswer ? <Check size={15} /> : showWrong || culled ? <X size={15} /> : INDEX[index]}
                    </span>
                    <span className="opt-body">
                      <span>{option.text}</span>
                      {why && (
                        <motion.small
                          className="opt-why"
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          transition={{ duration: 0.22 }}
                        >
                          {why}
                        </motion.small>
                      )}
                    </span>
                  </button>
                );
              })}
            </div>

            <AnimatePresence>
              {duel.result && (
                <motion.div
                  className={`ch-feedback ${duel.result}`}
                  tabIndex={-1}
                  role="region"
                  aria-label="作答解析"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  transition={{ duration: 0.25 }}
                >
                  <div className="fb-head">
                    {correct ? <Sparkles size={16} /> : <Target size={16} />}
                    <span>{correct ? (won ? "破關 · 四門皆破" : "命中要義") : "未中 · 再辨"}</span>
                  </div>
                  {correct && challenge && <AutoAdvance key={`${duel.analectId}:${challenge.gate}:${duel.selected}`} advance={() => actions.continueChallenge(challenge)} />}
                  <p className="fb-evidence">{challenge.evidence}</p>
                  {integrated && <p className="reading-note">先找到原文依據，再說明你排除錯項的理由。遊戲戰果不等同 My 審閱分數。</p>}
                  {challenge.purpose && <p className="fb-purpose">學習目的 · {challenge.purpose}</p>}
                  <button className="fb-next" onClick={() => correct && challenge ? actions.continueChallenge(challenge) : actions.close()}>
                    <span>{primaryLabel}</span>
                    <ArrowRight size={15} />
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.section>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
